// Cross-tab platform elevation lease (MBB-642).
//
// The lease lends the platform service account's super-admin authority to this
// human, under their own identity. auth_service verifies it on every request and
// re-derives the authority from the database, so the client never decides
// anything — it only decides whether to send the header.
//
// Stored in localStorage, not sessionStorage: an assumed session is a property of
// the signed-in user in this browser, not of a single tab, so every tab — including
// one opened after the assume — shares the same lease. It stays bounded: the lease
// is owner-bound and expiry-checked on every read, and cleared on logout/release,
// so it never outlives the user or the lease window.
//
// This lives in core, alongside the access token and CSRF handling, because it is a
// platform-session credential — not a domain concept. Both the host shell's session
// query and every MFE's request builders read the same lease; MFEs mount via Shadow
// DOM into the host document, so window/localStorage are shared.

export const ELEVATION_LEASE_TOKEN_KEY = 'platform_elevation_token';
export const ELEVATION_LEASE_OWNER_KEY = 'platform_elevation_owner';
export const ELEVATION_LEASE_EXPIRES_AT_KEY = 'platform_elevation_expires_at';

export interface ElevationLease {
	token: string;
	ownerUserId: string;
	expiresAt: string;
}

// SSR- and exception-safe accessor (private mode / disabled storage). Mirrors the
// sibling platform-session services in this directory (sessionPresence, identityHint).
const readStorage = (): Storage | null => {
	try {
		return typeof window !== 'undefined' ? window.localStorage : null;
	} catch {
		return null;
	}
};

export function clearElevationLease(): void {
	const storage = readStorage();
	if (!storage) return;
	storage.removeItem(ELEVATION_LEASE_TOKEN_KEY);
	storage.removeItem(ELEVATION_LEASE_OWNER_KEY);
	storage.removeItem(ELEVATION_LEASE_EXPIRES_AT_KEY);
}

/** A half-written lease is not a lease: it can be sent but never expired. */
export function writeElevationLease(lease: ElevationLease): void {
	const storage = readStorage();
	if (!storage) return;
	// Don't leave the previous lease riding requests: an incomplete write (e.g. a
	// malformed / deploy-skewed mint response) must clear the stale credential
	// rather than silently keep the old, possibly still-valid, token in place.
	if (!lease.token || !lease.ownerUserId || !lease.expiresAt) {
		clearElevationLease();
		return;
	}
	storage.setItem(ELEVATION_LEASE_TOKEN_KEY, lease.token);
	storage.setItem(ELEVATION_LEASE_OWNER_KEY, lease.ownerUserId);
	storage.setItem(ELEVATION_LEASE_EXPIRES_AT_KEY, lease.expiresAt);
}

/**
 * The lease, but only if it belongs to the signed-in user and has not expired.
 *
 * The owner binding is not decoration. The lease now outlives a tab (localStorage)
 * and a logout: without it, the next person to sign in on this browser inherits a
 * super-admin session they never assumed.
 */
export function resolveOwnedElevationLease(
	currentUserId: string | null | undefined,
): { token: string; expiresAt: string } | null {
	const storage = readStorage();
	if (!storage) return null;
	const token = storage.getItem(ELEVATION_LEASE_TOKEN_KEY);
	const owner = storage.getItem(ELEVATION_LEASE_OWNER_KEY);
	const expiresAt = storage.getItem(ELEVATION_LEASE_EXPIRES_AT_KEY);
	const expiresAtMs = expiresAt ? Date.parse(expiresAt) : Number.NaN;
	const expired = !Number.isFinite(expiresAtMs) || expiresAtMs <= Date.now();

	// Purge only a definitely-dead lease (expired, or owned by a different *known*
	// user). When the current user is unknown (cold refresh, before auth hydrates),
	// keep it — purging then would drop the user to base role on every reload.
	if (expired || (!!currentUserId && !!owner && owner !== currentUserId)) {
		if (token || owner || expiresAt) clearElevationLease();
		return null;
	}

	if (!token || !currentUserId || owner !== currentUserId) return null;
	return { token, expiresAt: expiresAt as string };
}

/** The header every request builder attaches. Absent lease ⇒ absent header. */
export function platformElevationHeader(
	currentUserId: string | null | undefined,
): Record<string, string> {
	const lease = resolveOwnedElevationLease(currentUserId);
	if (!lease) return {};
	return { 'x-platform-elevation': lease.token };
}
